// What happened in a window, newest first, as on the phone (ui/common/Timeline.kt). Rows and the
// chart point at each other: hovering a row marks its time on the chart, and selecting an item on
// the chart scrolls its row into view.

import { ChevronRight } from "lucide-react";
import { useEffect, useRef } from "react";
import type { TimelineRow } from "../../shared/timeline.js";
import { entryEmoji, headline, pumpSummary } from "../../shared/labels.js";
import { cn } from "@/lib/cn";
import { approxSpan, entryTimeText, formatTime } from "@/lib/format";

interface Props {
  rows: TimelineRow[];
  zone: string;
  selectedKey?: string | null;
  onSelect?: (key: string | null) => void;
  onHover?: (t: number | null) => void;
  /** Opens an entry's or a note's own page; rows without an id can't be opened. */
  onOpen?: (row: TimelineRow) => void;
  className?: string;
  empty?: string;
}

function canOpen(row: TimelineRow): boolean {
  return row.kind === "entry" || (row.kind === "note" && row.note.id !== null);
}

export function Timeline({ rows, zone, selectedKey = null, onSelect, onHover, onOpen, className, empty = "Nothing logged." }: Props) {
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = scroller.current;
    if (!container || !selectedKey) return;
    const row = container.querySelector<HTMLElement>(`[data-key="${CSS.escape(selectedKey)}"]`);
    if (row) scrollWithin(container, row);
  }, [selectedKey]);

  return (
    <div ref={scroller} className={className}>
      {rows.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-muted">{empty}</p>
      ) : (
        <ol className="py-1">
          {rows.map((row) => (
            <li key={row.key} data-key={row.key} className={cn("group flex", selectedKey === row.key && "bg-primary-soft")}>
              <button
                type="button"
                className={cn(
                  "flex min-w-0 flex-1 items-start gap-3 py-2.5 pl-4 text-left transition-colors hover:bg-surface-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary",
                  onOpen && canOpen(row) ? "pr-1" : "pr-4",
                  selectedKey === row.key && "hover:bg-primary-soft",
                )}
                aria-pressed={selectedKey === row.key}
                onClick={() => onSelect?.(selectedKey === row.key ? null : row.key)}
                onMouseEnter={() => onHover?.(row.t)}
                onMouseLeave={() => onHover?.(null)}
                onFocus={() => onHover?.(row.t)}
                onBlur={() => onHover?.(null)}
              >
                <RowIcon row={row} />
                <span className="min-w-0 flex-1">
                  <span className="line-clamp-2 text-sm">{rowHeadline(row)}</span>
                  <span className="block text-xs text-muted" title={rowTimeHint(row)}>
                    {rowSupporting(row, zone)}
                  </span>
                </span>
              </button>
              {onOpen && canOpen(row) && (
                <button
                  type="button"
                  aria-label="Open"
                  title="Open: glucose before and after"
                  onClick={() => onOpen(row)}
                  className="grid w-10 shrink-0 place-items-center text-muted transition-colors hover:bg-surface-muted hover:text-fg focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary [&_svg]:size-4"
                >
                  <ChevronRight />
                </button>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function rowHeadline(row: TimelineRow): string {
  switch (row.kind) {
    case "entry":
      return headline(row.entry);
    case "pump":
      return pumpSummary(row.pump);
    case "note":
      return row.note.text;
  }
}

function rowSupporting(row: TimelineRow, zone: string): string {
  switch (row.kind) {
    case "entry":
      return entryTimeText(row.entry, zone);
    case "pump":
      return `${formatTime(row.t, zone)} · pump`;
    case "note":
      return `${formatTime(row.t, zone)} · ${row.note.enteredBy ?? "Nightscout"}`;
  }
}

function rowTimeHint(row: TimelineRow): string | undefined {
  return row.kind === "entry" && row.entry.approx !== null ? `Approximate time (${approxSpan(row.entry.approx)})` : undefined;
}

function RowIcon({ row }: { row: TimelineRow }) {
  if (row.kind === "entry") {
    return (
      <span className="grid size-9 shrink-0 place-items-center rounded-full text-lg" style={{ background: "var(--chart-marker)" }} aria-hidden>
        {entryEmoji(row.entry)}
      </span>
    );
  }
  if (row.kind === "pump") {
    return (
      <span className="flex size-9 shrink-0 items-end justify-center gap-[3px] pb-2.5" aria-hidden>
        {row.pump.carbs !== null && <span className="h-5 w-[5px] rounded-[1px]" style={{ background: "var(--chart-carbs)" }} />}
        {row.pump.insulin !== null && <span className="h-[13px] w-1 rounded-[1px]" style={{ background: "var(--chart-insulin)" }} />}
      </span>
    );
  }
  return (
    <span className="grid size-9 shrink-0 place-items-center" aria-hidden>
      <span className="size-2.5 rounded-full" style={{ background: "var(--chart-other-note)" }} />
    </span>
  );
}

/** Scrolls `row` into view inside `container` only, never the page, which would jump on phones. */
function scrollWithin(container: HTMLElement, row: HTMLElement) {
  if (container.scrollHeight <= container.clientHeight) return;
  const c = container.getBoundingClientRect();
  const r = row.getBoundingClientRect();
  if (r.top < c.top) container.scrollTop += r.top - c.top - 8;
  else if (r.bottom > c.bottom) container.scrollTop += r.bottom - c.bottom + 8;
}
